import { debounce } from "lodash"
import { useEffect, useRef } from "react"

const AUTH_TIMEOUT_MIN = 5
const ACTIVITY_DEBOUNCE_MS = 5000
const ACTIVITY_EVENTS = ["mousemove", "keydown"]

const DEBUG_MODE: boolean = import.meta.env.VITE_DEBUG_MODE === "true"

const useActivityMonitor = (
  isPinLoggedIn: boolean,
  onPinLogout: () => void
) => {
  // Read through a ref so a new callback identity cannot rebuild the effect
  // mid-session, which is a second way to reach the teardown below.
  const onPinLogoutRef = useRef(onPinLogout)
  useEffect(() => {
    onPinLogoutRef.current = onPinLogout
  }, [onPinLogout])

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>

    const resetTimeout = () => {
      clearTimeout(timeout)
      timeout = setTimeout(
        () => {
          onPinLogoutRef.current()
        },
        AUTH_TIMEOUT_MIN * 60 * 1000
      )
    }

    const handleActivity = debounce(
      () => {
        resetTimeout()
      },
      ACTIVITY_DEBOUNCE_MS,
      // Debounce waits indefinitely until activity stops without this line
      { maxWait: ACTIVITY_DEBOUNCE_MS }
    )

    if (isPinLoggedIn && !DEBUG_MODE) {
      // Set initial timeout
      resetTimeout()

      // Add activity listeners
      ACTIVITY_EVENTS.forEach((event) => {
        document.addEventListener(event, handleActivity, true)
      })
    }

    return () => {
      // Must come first. `isPinLoggedIn` flips on every pin logout, so this
      // teardown runs on each user handoff while the debounce is still holding
      // a trailing invocation from the outgoing teacher's last activity. Left
      // pending, it fires after cleanup and re-arms `timeout` in a closure
      // whose listeners are already detached — an unresettable logout that
      // lands on whoever signs in next, mid-keystroke. They also stack, one
      // per handoff.
      handleActivity.cancel()
      clearTimeout(timeout)
      ACTIVITY_EVENTS.forEach((event) => {
        document.removeEventListener(event, handleActivity, true)
      })
    }
  }, [isPinLoggedIn])
}

export default useActivityMonitor
